import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Filter, 
  Search, 
  Play, 
  Clock, 
  Star,
  Zap,
  CheckCircle
} from 'lucide-react';

export default function Missions() {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const missions = [
    {
      id: 1,
      title: 'Bring Your Own Bag',
      description: 'Use reusable bags for shopping instead of plastic bags',
      category: 'Shopping',
      difficulty: 'Easy',
      points: 50,
      timeFrame: 'Daily',
      icon: '🛍️',
      trending: true,
      completed: false,
      arEnabled: true
    },
    {
      id: 2,
      title: 'Plant-Based Meal',
      description: 'Share a photo of your delicious meat-free meal',
      category: 'Food',
      difficulty: 'Easy',
      points: 75,
      timeFrame: 'Daily',
      icon: '🥗',
      trending: false,
      completed: false,
      arEnabled: true
    },
    {
      id: 3,
      title: 'Car-Free Commute',
      description: 'Walk, bike, or use public transport instead of driving',
      category: 'Transport',
      difficulty: 'Medium',
      points: 100,
      timeFrame: 'Daily',
      icon: '🚲',
      trending: true,
      completed: false,
      arEnabled: false
    },
    {
      id: 4,
      title: 'Community Clean-Up',
      description: 'Organize or join a local clean-up event',
      category: 'Community',
      difficulty: 'Hard',
      points: 300,
      timeFrame: 'Monthly',
      icon: '🧹',
      trending: false,
      completed: true,
      arEnabled: false
    },
    {
      id: 5,
      title: 'Zero Waste Week',
      description: 'Go completely zero waste for an entire week',
      category: 'Waste',
      difficulty: 'Hard',
      points: 1000,
      timeFrame: 'Weekly',
      icon: '♻️',
      trending: true,
      completed: false,
      arEnabled: true,
      comingSoon: true,
      launchDate: '2025-01-15'
    }
  ];

  const categories = ['all', 'Shopping', 'Food', 'Transport', 'Community', 'Waste'];

  const filteredMissions = missions.filter(mission => {
    const matchesFilter = filter === 'all' || mission.category === filter;
    const matchesSearch = mission.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="h-full mobile-scroll custom-scrollbar">
      <div className="p-4 space-y-4 pb-6">
        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-3"
        >
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search missions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-4 py-2 rounded-full whitespace-nowrap font-medium transition-colors ${
                  filter === category
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                {category === 'all' ? 'All' : category}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-3 gap-4"
        >
          <div className="bg-white rounded-2xl p-4 text-center card-shadow">
            <p className="text-2xl font-bold text-emerald-600">12</p>
            <p className="text-sm text-gray-600">Available</p>
          </div>
          <div className="bg-white rounded-2xl p-4 text-center card-shadow">
            <p className="text-2xl font-bold text-blue-600">8</p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
          <div className="bg-white rounded-2xl p-4 text-center card-shadow">
            <p className="text-2xl font-bold text-purple-600">3</p>
            <p className="text-sm text-gray-600">Trending</p>
          </div>
        </motion.div>

        {/* Missions List - Now in row format */}
        <div className="space-y-4">
          {filteredMissions.map((mission, index) => (
            <motion.div
              key={mission.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
              className={`bg-white rounded-2xl p-4 card-shadow ${
                mission.completed ? 'opacity-75' : ''
              } ${mission.comingSoon ? 'border-2 border-purple-200 bg-purple-50' : ''}`}
            >
              {/* Coming Soon Badge */}
              {mission.comingSoon && (
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    COMING SOON
                  </span>
                  <span className="text-sm text-purple-600 font-medium">
                    Launches Jan 15
                  </span>
                </div>
              )}

              <div className="flex items-start space-x-4">
                <div className="text-4xl">{mission.icon}</div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-gray-900">{mission.title}</h3>
                        {mission.trending && (
                          <div className="flex items-center space-x-1">
                            <Star className="h-4 w-4 text-orange-500 fill-current" />
                            <span className="text-xs text-orange-600 font-medium">Trending</span>
                          </div>
                        )}
                        {mission.arEnabled && (
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full">
                            <span className="text-xs font-bold">AR</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{mission.description}</p>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(mission.difficulty)}`}>
                      {mission.difficulty}
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                      {mission.timeFrame}
                    </span>
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                      {mission.category}
                    </span>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-emerald-600">
                      +{mission.points} EcoCoins
                    </span>
                    
                    {mission.completed ? (
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircle className="h-5 w-5" />
                        <span className="text-sm font-medium">Completed</span>
                      </div>
                    ) : mission.comingSoon ? (
                      <button className="bg-gray-300 text-gray-500 px-4 py-2 rounded-xl font-medium flex items-center space-x-1 cursor-not-allowed">
                        <Clock className="h-4 w-4" />
                        <span>Soon</span>
                      </button>
                    ) : (
                      <button className="bg-emerald-500 text-white px-4 py-2 rounded-xl font-medium flex items-center space-x-1 ripple">
                        {mission.arEnabled ? (
                          <>
                            <Zap className="h-4 w-4" />
                            <span>Start AR</span>
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4" />
                            <span>Start</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {filteredMissions.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No missions found</h3>
            <p className="text-gray-600">Try adjusting your search or filter</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}